/**
 * THE leave-type icon map — single source of truth.
 *
 * Why this exists (Rob, 16 Jul): Settings' icon picker offers ~50 icons, but
 * FOUR separate hand-rolled copies of a ~15-icon lookup lived in
 * DashboardPage, MyLeavePage, UserLeavePage and QuickBookPopover. Pick any
 * icon the copies didn't know and every calendar silently fell back to the
 * default — "I changed the icon and nothing happened."
 *
 * This module carries every icon the Settings picker offers (keep the two in
 * lockstep — if you add one there, add it here) plus the legacy aliases old
 * data may contain ('palm', 'house', 'graduation'). Unknown names still fall
 * back to Calendar rather than crashing.
 */
import React from 'react';
import {
  Calendar, Palmtree, Plane, Umbrella, Sun, Sunset, Mountain, Waves, Tent,
  Heart, HeartPulse, Stethoscope, Pill, Thermometer, Activity, Cross,
  Baby, Home, UserPlus, Users2, HeartHandshake,
  Laptop, Coffee, Monitor, Wifi, Building, Briefcase,
  GraduationCap, BookOpen, Award, School,
  Gift, PartyPopper, Cake, Star,
  Car, Train, Ship,
  Snowflake, CloudRain, Leaf,
  Bed, Moon, Sofa, Dumbbell, Bike,
  CalendarDays, CalendarCheck, CalendarX, Clock, CircleDot,
  Church, Sparkles, Flame, FlameKindling, HandHeart, Sunrise, Feather, Flower2,
} from 'lucide-react';

export const LEAVE_TYPE_ICONS = {
  // Vacation & Travel
  palmtree: Palmtree, plane: Plane, umbrella: Umbrella, sun: Sun,
  sunset: Sunset, mountain: Mountain, waves: Waves, tent: Tent,
  // Medical & Health
  heart: Heart, heartpulse: HeartPulse, stethoscope: Stethoscope, pill: Pill,
  thermometer: Thermometer, activity: Activity, cross: Cross,
  // Family & Personal
  baby: Baby, home: Home, userplus: UserPlus, users2: Users2,
  hearthandshake: HeartHandshake,
  // Work & Remote
  laptop: Laptop, coffee: Coffee, monitor: Monitor, wifi: Wifi,
  building: Building, briefcase: Briefcase,
  // Education & Training
  graduationcap: GraduationCap, bookopen: BookOpen, award: Award, school: School,
  // Celebrations
  gift: Gift, partypopper: PartyPopper, cake: Cake, star: Star,
  // Transport
  car: Car, train: Train, ship: Ship,
  // Weather / Seasons
  snowflake: Snowflake, cloudrain: CloudRain, leaf: Leaf,
  // Rest & Wellness
  bed: Bed, moon: Moon, sofa: Sofa, dumbbell: Dumbbell, bike: Bike,
  // Calendar & General
  calendar: Calendar, calendardays: CalendarDays, calendarcheck: CalendarCheck,
  calendarx: CalendarX, clock: Clock, circledot: CircleDot,
  // Religion & Faith
  church: Church, religioncross: Cross, sparkles: Sparkles, flame: Flame,
  flamekindling: FlameKindling, handheart: HandHeart, sunrise: Sunrise,
  crescent: Moon, starfaith: Star, feather: Feather, flower2: Flower2,
  // Legacy aliases (old stored data)
  palm: Palmtree, house: Home, graduation: GraduationCap,
};

/** The Lucide component for a stored icon name (fallback: Calendar). */
export function leaveTypeIconComponent(iconName) {
  return LEAVE_TYPE_ICONS[String(iconName || '').toLowerCase()] || Calendar;
}

/** Rendered icon element — drop-in for the old per-page getLeaveTypeIcon. */
export function getLeaveTypeIcon(iconName, className = 'w-4 h-4 text-white') {
  const IconComponent = leaveTypeIconComponent(iconName);
  return <IconComponent className={className} />;
}


// Navy-or-white for an icon on a leave colour (Rob's 4A + 9A, 29/10/2026) — pure, in its own file
// so `node src/lib/leaveColour.checks.mjs` can test it without JSX.
export { isPaleColour, iconOn } from './leaveColour';
